// Cliente de la API. Una sola puerta: llamar(nombreDeRuta, …). Las rutas y sus permisos salen de RUTAS y de
// RUTAS_PREPARADAS (contrato v2: rutas ya definidas que el integrador mueve a RUTAS en cuanto el servidor las tiene)
// de @cotizador/compartido/api.ts, así la interfaz no puede pedir una URL que el servidor no conoce.
// Errores: TODO error del servidor llega como ErrorDeApi con el código y el mensaje del servidor (español, para la
// persona). Un error que no tiene esa forma también truena (nunca se devuelve "vacío" como si hubiera salido bien).
import { RUTAS, RUTAS_PREPARADAS, esErrorApi, rutaCon } from '@cotizador/compartido';
import type { CodigoError, NombreRuta, NombreRutaPreparada, Ruta } from '@cotizador/compartido';

/** Cualquier ruta del contrato: de RUTAS o, mientras el integrador no las mueva, de RUTAS_PREPARADAS. */
export type NombreDeRuta = NombreRuta | NombreRutaPreparada;
const TODAS: Readonly<Record<NombreDeRuta, Ruta>> = Object.freeze({ ...RUTAS, ...RUTAS_PREPARADAS });

/** Método, ruta y permiso de una ruta. Truena si el nombre no está en ninguna de las dos tablas (fallo ruidoso). */
export function rutaDe(nombre: NombreDeRuta): Ruta {
  const r = TODAS[nombre];
  if (!r) throw new Error(`La ruta "${String(nombre)}" no está en RUTAS ni en RUTAS_PREPARADAS de @cotizador/compartido.`);
  return r;
}

export type CodigoCliente = CodigoError | 'RED' | 'RESPUESTA_RARA';

export class ErrorDeApi extends Error {
  readonly codigo: CodigoCliente;
  readonly detalles: string[];
  readonly estado: number;
  constructor(codigo: CodigoCliente, mensaje: string, detalles: string[] = [], estado = 0) {
    super(mensaje);
    this.name = 'ErrorDeApi';
    this.codigo = codigo;
    this.detalles = detalles;
    this.estado = estado;
  }
  /** Otra persona guardó antes: hay que recargar la cotización. */
  get esConflicto(): boolean {
    return this.codigo === 'CONFLICTO' || this.codigo === 'EDICION_DESACTUALIZADA';
  }
}

/** Cómo viaja una petición. En el navegador es fetch; con VITE_API_SIMULADA=si (y en pruebas) es el servidor simulado. */
export type Transporte = (url: string, init: RequestInit) => Promise<Response>;

let transporte: Transporte = (url, init) => fetch(url, init);
let esSimulado = false;

export function usarTransporte(t: Transporte, simulado: boolean): void {
  transporte = t;
  esSimulado = simulado;
}
export function transporteSimulado(): boolean {
  return esSimulado;
}

export interface OpcionesLlamada {
  params?: Record<string, string | number>;
  consulta?: Record<string, string | number | null | undefined>;
  cuerpo?: unknown;
  formulario?: FormData;
  senal?: AbortSignal;
  /** La petición sigue aunque la página se vaya a segundo plano (p. ej. al abrir WhatsApp en el iPhone). Solo cuerpos chicos. */
  keepalive?: boolean;
}

/** Se dispara cuando el servidor dice que no hay sesión (la app vuelve a la pantalla de entrar). */
export const EVENTO_SIN_SESION = 'cotizador:sin-sesion';

export function urlDe(nombre: NombreDeRuta, op: Pick<OpcionesLlamada, 'params' | 'consulta'> = {}): string {
  let url = rutaCon(nombre, op.params);
  if (op.consulta) {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(op.consulta)) if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    const s = q.toString();
    if (s) url += `?${s}`;
  }
  return url;
}

export async function llamar<T>(nombre: NombreDeRuta, op: OpcionesLlamada = {}): Promise<T> {
  const ruta = rutaDe(nombre);
  const url = urlDe(nombre, op);
  const headers: Record<string, string> = { accept: 'application/json' };
  const init: RequestInit = { method: ruta.metodo, credentials: 'same-origin', headers };
  if (op.senal) init.signal = op.senal;
  if (op.keepalive) init.keepalive = true;
  if (op.formulario) init.body = op.formulario;
  else if (op.cuerpo !== undefined) {
    headers['content-type'] = 'application/json';
    init.body = JSON.stringify(op.cuerpo);
  }

  let resp: Response;
  try {
    resp = await transporte(url, init);
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    // Hallazgo del 10-oct: "TypeError: Failed to fetch" (o "Load failed" en iPhone) no le dice nada al vendedor; queda en la
    // consola del navegador para quien la revise.
    console.error('[red]', ruta.metodo, ruta.ruta, e);
    throw new ErrorDeApi('RED', 'No hay conexión con el servidor del cotizador. Revisa tu internet y vuelve a intentar.');
  }

  const texto = await resp.text();
  let cuerpo: unknown = null;
  if (texto) {
    try {
      cuerpo = JSON.parse(texto);
    } catch {
      throw new ErrorDeApi(
        'RESPUESTA_RARA',
        `El servidor respondió algo que no es JSON (${resp.status}) a ${ruta.metodo} ${ruta.ruta}.`,
        [texto.slice(0, 300)],
        resp.status,
      );
    }
  }

  if (!resp.ok) {
    if (resp.status === 401 && typeof window !== 'undefined') window.dispatchEvent(new Event(EVENTO_SIN_SESION));
    if (esErrorApi(cuerpo)) {
      const e = cuerpo.error;
      throw new ErrorDeApi(e.codigo, e.mensaje, Array.isArray(e.detalles) ? e.detalles : [], resp.status);
    }
    throw new ErrorDeApi('RESPUESTA_RARA', `El servidor respondió ${resp.status} sin explicación.`, [texto.slice(0, 300)], resp.status);
  }
  if (cuerpo === null) throw new ErrorDeApi('RESPUESTA_RARA', `El servidor no mandó respuesta a ${ruta.metodo} ${ruta.ruta}.`, [], resp.status);
  return cuerpo as T;
}

/** Bytes de un archivo guardado (recibo, PDF, portada) como Blob, por la misma puerta (sirve igual en modo simulado). */
export async function bajarArchivo(archivoId: string): Promise<Blob> {
  const url = rutaCon('descargarArchivo', { archivoId });
  let resp: Response;
  try {
    resp = await transporte(url, { method: 'GET', credentials: 'same-origin' });
  } catch (e) {
    console.error('[red] descargar', archivoId, e);
    throw new ErrorDeApi('RED', 'No se pudo bajar el archivo: no hay conexión con el servidor. Revisa tu internet y vuelve a intentar.');
  }
  if (!resp.ok) {
    const texto = await resp.text();
    let j: unknown = null;
    try {
      j = JSON.parse(texto);
    } catch {
      /* el mensaje va abajo */
    }
    if (esErrorApi(j)) throw new ErrorDeApi(j.error.codigo, j.error.mensaje, j.error.detalles, resp.status);
    throw new ErrorDeApi('RESPUESTA_RARA', `No se pudo bajar el archivo (${resp.status}).`, [], resp.status);
  }
  return resp.blob();
}

/** ADENDA z59: la hoja de la lista de compra (HTML) o el sobre del CRM (JSON) como Blob, por la misma puerta (sirve igual en modo
 *  simulado). Solo Diego: a cualquier otro el servidor responde 403 y aquí truena con su mensaje. */
export async function bajarListaCompra(id: string, version: 'hiram' | 'diego', formato: 'html' | 'sobre' = 'html'): Promise<Blob> {
  const url = urlDe('listaDeCompra', { params: { id }, consulta: { version, formato } });
  let resp: Response;
  try {
    resp = await transporte(url, { method: 'GET', credentials: 'same-origin' });
  } catch (e) {
    console.error('[red] lista de compra', id, e);
    throw new ErrorDeApi('RED', 'No se pudo bajar la lista de compra: no hay conexión con el servidor. Revisa tu internet y vuelve a intentar.');
  }
  if (!resp.ok) {
    const texto = await resp.text();
    let j: unknown = null;
    try {
      j = JSON.parse(texto);
    } catch {
      /* el mensaje va abajo */
    }
    if (esErrorApi(j)) throw new ErrorDeApi(j.error.codigo, j.error.mensaje, j.error.detalles, resp.status);
    throw new ErrorDeApi('RESPUESTA_RARA', `No se pudo bajar la lista de compra (${resp.status}).`, [], resp.status);
  }
  return resp.blob();
}

/** Mensaje para la persona a partir de cualquier error. */
export function mensajeDe(e: unknown): string {
  if (e instanceof Error) return e.message;
  return String(e);
}
